// Single definition of what counts as a "flagged issue".
//
// Every page that shows an issue count must agree, or a tutor shows "7 issues"
// in one place and "0" when you click through. Two rules, applied everywhere:
//
//   1. tutor_lateness is NOT an issue. Lateness is presented on its own
//      Punctuality surfaces; counting it here double-reports it.
//   2. Guardrail-suppressed risks are NOT issues. Suppression is decided by an
//      LLM that stamps risks_v2.filter_status — never by keyword matching here.
//
// Status buckets: `new` (and anything unrecognised) is open, so a bad status
// value fails visible rather than silently vanishing.

import { applyIssuePolicy } from './issuePolicy';

// Columns a query must select for these helpers to work. filter_status drives
// suppression, category drives the lateness rule.
export const ISSUE_SELECT_COLUMNS =
  'id, session_id, status, category, severity, filter_status, filter_decision_reason';

export const LATENESS_CATEGORY = 'tutor_lateness';

export const isLatenessRisk = (r) => r?.category === LATENESS_CATEGORY;

// Risks that belong on issue surfaces at all: non-lateness and not suppressed.
export const countableIssues = (risks, policy) => {
  if (!Array.isArray(risks)) return [];
  const nonLateness = risks.filter((r) => r && !isLatenessRisk(r));
  return applyIssuePolicy(nonLateness, policy).visible;
};

export const suppressedIssues = (risks, policy) => {
  if (!Array.isArray(risks)) return [];
  const nonLateness = risks.filter((r) => r && !isLatenessRisk(r));
  return applyIssuePolicy(nonLateness, policy).suppressed;
};

export const OPEN_STATUSES = ['new'];
export const UNDER_REVIEW_STATUSES = ['under_review'];
// 'actioned' and 'non_issue' are legacy values the intelligence-chat functions
// and the Issues page already treat as closed; without them here they would
// count as open and appear in no Issues tab.
export const CLOSED_STATUSES = ['resolved', 'dismissed', 'actioned', 'non_issue'];

export const bucketForStatus = (status) => {
  const s = (status || 'new').toLowerCase();
  if (UNDER_REVIEW_STATUSES.includes(s)) return 'under_review';
  if (CLOSED_STATUSES.includes(s)) return 'closed';
  return 'open';
};

export const isOpenIssue = (r) => bucketForStatus(r?.status) === 'open';

// { open, under_review, closed } for the countable set.
export const partitionIssues = (risks, policy) => {
  const groups = { open: [], under_review: [], closed: [] };
  for (const r of countableIssues(risks, policy)) groups[bucketForStatus(r.status)].push(r);
  return groups;
};

// The number to show on a KPI tile or badge: open issues only.
export const countOpenIssues = (risks, policy) => countableIssues(risks, policy).filter(isOpenIssue).length;

// Total issues worth surfacing, whatever their status — use where a page shows
// "N total" alongside the open count.
export const countAllIssues = (risks, policy) => countableIssues(risks, policy).length;
